import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2, Pencil } from 'lucide-react';
import { updateContact } from '@/lib/clientsData';
import { LEAD_SOURCE_OPTIONS } from '@/components/clients/AddLeadDialog';
import { toast } from 'sonner';

const toForm = (c) => ({
  name: c?.name || '',
  phone: c?.phone || '',
  email: c?.email || '',
  address: c?.address || '',
  type: c?.type || 'lead',
  source: c?.source || '',
  ltv: c?.ltv ?? '',
  last_contact: c?.last_contact || '',
  notes: Array.isArray(c?.notes) ? c.notes.join('\n') : c?.notes || '',
});

export const EditContactDialog = ({ contact, open, onOpenChange, onUpdated }) => {
  const [form, setForm] = useState(() => toForm(contact));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(toForm(contact));
      setError('');
      setSubmitting(false);
    }
  }, [open, contact]);

  const setField = (key) => (e) => {
    const v = e?.target?.value ?? '';
    setForm((f) => ({ ...f, [key]: v }));
  };

  const validate = () => {
    if (!form.name.trim()) return 'Name is required.';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) return 'Email looks invalid.';
    if (form.ltv !== '' && Number.isNaN(Number(form.ltv)))
      return 'Lifetime value must be a number.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const updated = await updateContact(contact.id, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        type: form.type,
        source: form.source.trim(),
        ltv: Number(form.ltv || 0),
        last_contact: form.last_contact.trim(),
        notes: form.notes,
      });
      toast.success(`Saved \u2014 ${updated.name}`);
      onOpenChange?.(false);
      onUpdated?.(updated);
    } catch (err) {
      setError(err?.message || 'Failed to save changes.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!contact) return null;
  const isLead = form.type === 'lead';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))] max-w-lg"
        data-testid="edit-contact-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
            <Pencil size={16} strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
              Edit Contact
            </span>
          </div>
          <DialogTitle className="text-foreground tracking-tight">
            {contact?.name || 'Edit contact'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Update contact details. Changes save to Supabase immediately.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-1" noValidate>
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Name *
            </Label>
            <Input
              value={form.name}
              onChange={setField('name')}
              autoComplete="off"
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
              data-testid="edit-contact-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Type
              </Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
              >
                <SelectTrigger
                  className="h-10 bg-[hsl(var(--muted))] border-border"
                  data-testid="edit-contact-type"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Last contact
              </Label>
              <Input
                value={form.last_contact}
                onChange={setField('last_contact')}
                placeholder="e.g. Apr 7"
                autoComplete="off"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="edit-contact-last-contact"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Phone
              </Label>
              <Input
                value={form.phone}
                onChange={setField('phone')}
                autoComplete="off"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="edit-contact-phone"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Email
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={setField('email')}
                autoComplete="off"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="edit-contact-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Address
            </Label>
            <Input
              value={form.address}
              onChange={setField('address')}
              autoComplete="off"
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
              data-testid="edit-contact-address"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                How did you hear about us?
              </Label>
              <Select
                value={form.source}
                onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}
              >
                <SelectTrigger
                  className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                  data-testid="edit-contact-source"
                >
                  <SelectValue placeholder="Select a source…" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  {LEAD_SOURCE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isLead ? (
              <div className="space-y-2">
                <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  Lifetime value (USD)
                </Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="100"
                  value={form.ltv}
                  onChange={setField('ltv')}
                  className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                  data-testid="edit-contact-value"
                />
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Notes
            </Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={setField('notes')}
              placeholder="One note per line."
              className="bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 resize-none"
              data-testid="edit-contact-notes"
            />
          </div>

          {error ? (
            <Alert
              className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
              data-testid="edit-contact-error"
            >
              <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="edit-contact-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving
                </>
              ) : (
                <>Save changes</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditContactDialog;
